"use client"
import { useRouter, usePathname, useParams } from 'next/navigation';
import NextLink from 'next/link';
import { ReactNode, useEffect, useState } from "react";
import React from "react";

// Use Next.js router hook with wouter-like typed return
function useLocation(): [string, (to: string) => void] {
  const router = useRouter();
  const currentPathname = usePathname();
  const [pathname, setPathname] = useState("/");
  
  useEffect(() => {
    // Update pathname when it's available
    if (currentPathname) {
      setPathname(currentPathname);
    }
  }, [currentPathname]);
  
  const navigate = (to: string) => {
    router.push(to);
  };
  
  return [pathname, navigate];
}

// Wrapper for Next.js Link component with wouter-like props
interface LinkProps {
  href: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}

function Link({ href, children, className, onClick }: LinkProps) {
  return (
    <NextLink href={href} className={className} onClick={onClick}>
      {children}
    </NextLink>
  );
}

// Navigation hook for programmatic navigation
function useNavigate() {
  const router = useRouter();
  
  return (to: string) => {
    router.push(to);
  };
}

// Route and Switch components to maintain compatibility with wouter
interface RouteProps {
  path?: string;
  component: React.ComponentType<any>;
}

function Route({ path, component: Component }: RouteProps) {
  const pathname = usePathname();
  // Only render component if path matches current pathname
  return pathname === path ? <Component /> : null;
}

interface SwitchProps {
  children: React.ReactNode;
}

function Switch({ children }: SwitchProps) {
  const pathname = usePathname();
  
  // Find the first matching route and render only that one
  const childrenArray = React.Children.toArray(children);
  const matchedChild = childrenArray.find((child) => {
    if (React.isValidElement(child)) {
      // Type assertion to access props safely
      const childElement = child as React.ReactElement<RouteProps>;
      return childElement.props.path === pathname;
    }
    return false;
  });
  
  return <>{matchedChild}</>;
}

// Export all components and functions
export {
  useLocation,
  useNavigate,
  useParams,
  Link,
  Route,
  Switch
};
